/**
 * Sender-string parsing helpers — extracted verbatim from EmailPanel.tsx.
 *
 * NOTE: `extractSenderName`/`extractSenderEmail` are also duplicated in
 * ThreadView.tsx. Cross-file unification is tracked as a follow-up
 * (small-dup beats wrong-abstraction).
 */
import type { RecipientFieldValue } from '@/lib/api/endpoints/email-module';

/** Pull the bare email out of one recipient token (`"Name" <email>` → email,
 *  `email` → email). Mirrors the established `openDraftInCompose` extraction. */
function addressOfPart(part: string): string {
  const trimmed = part.trim();
  const match = trimmed.match(/<([^>]+)>/);
  return (match ? match[1] : trimmed).trim();
}

/** Normalize a recipient field — which is a comma-joined string from the THREAD
 *  route but an `EmailAddress[]` from the DRAFT route — into a flat list of
 *  address strings. The single safe entry point for any scalar op on a `to`
 *  field; replaces unguarded `to.split(',')` / `to.match(...)` that crash on the
 *  array shape (#3756 string-vs-object class). */
export function toRecipientList(to: RecipientFieldValue | null | undefined): string[] {
  if (!to) return [];
  const parts = Array.isArray(to)
    ? to.map((e) => (typeof e === 'string' ? e : e?.address || e?.email || ''))
    : to.split(',');
  return parts.map(addressOfPart).filter(Boolean);
}

/** First recipient address (`''` when none). */
export function firstRecipient(to: RecipientFieldValue | null | undefined): string {
  return toRecipientList(to)[0] || '';
}

export function extractSenderName(from: string): string {
  // "John Doe <john@example.com>" → "John Doe"
  const match = from.match(/^"?([^"<]+)"?\s*</);
  if (match) return match[1].trim();
  // "john@example.com" → "john"
  const emailMatch = from.match(/^([^@]+)@/);
  if (emailMatch) return emailMatch[1];
  return from;
}

export function extractSenderEmail(from: string): string {
  const match = from.match(/<([^>]+)>/);
  if (match) return match[1];
  if (from.includes('@')) return from.trim();
  return '';
}

export function getSenderHideKey(from: string): string {
  const email = extractSenderEmail(from).toLowerCase();
  if (!email) return from.toLowerCase();
  const domain = email.split('@')[1] || '';
  // For common free email providers, hide by full address (personal accounts)
  const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com', 'protonmail.com', 'me.com'];
  if (personalDomains.includes(domain)) {
    return email;
  }
  // For company/service domains, hide by domain
  return `@${domain}`;
}

export function senderInitial(from: string): string {
  const name = extractSenderName(from);
  return name.charAt(0).toUpperCase();
}

// Extracted verbatim from EmailPanel.tsx (#3278 decomposition)
/** Parse a raw recipient header (`"Name" <email>`, `<email>`, `email`, or a
 *  comma-list) into a display name + email. Used by the Sent folder to show WHO
 *  a message went to — the sender is always the user, so showing it would put
 *  the user's avatar on every Sent item. */
export function parseRecipientHeader(to: string | undefined): { name: string; email: string } {
  const raw = (to || '').trim();
  if (!raw) return { name: '', email: '' };
  const named = raw.match(/^"?([^"<]+?)"?\s*<([^>]+)>/);
  if (named) return { name: named[1].trim(), email: named[2].trim() };
  const angle = raw.match(/<([^>]+)>/);
  if (angle) return { name: '', email: angle[1].trim() };
  return { name: '', email: raw.split(',')[0].trim() };
}
